import api from './api';

export interface PublicApiScopeCatalogItem {
  code: string;
  label: string;
}

export interface PublicApiCredentialSummary {
  id: string;
  name: string;
  keyId: string;
  secretLast4: string;
  scopes: string[];
  allowedIps: string[];
  rateLimitPerMinute: number;
  isActive: boolean;
  expiresAt: string | null;
  lastUsedAt: string | null;
  lastUsedIp: string | null;
  createdByName: string | null;
  revokedByName: string | null;
  revokedAt: string | null;
  revokeReason: string;
  createdAt: string;
  updatedAt: string;
}

export interface PublicApiCredentialAuditEntry {
  id: string;
  action: string;
  actorUserName: string | null;
  payload: Record<string, unknown>;
  createdAt: string;
}

export interface PublicApiCredentialListResponse {
  credentials: PublicApiCredentialSummary[];
  availableScopes: PublicApiScopeCatalogItem[];
}

export interface PublicApiCredentialIssueResponse {
  credential: PublicApiCredentialSummary;
  bearerToken: string;
}

export interface PublicApiCredentialPayload {
  name: string;
  scopes: string[];
  allowedIps: string[];
  rateLimitPerMinute?: number;
  expiresAt: string | null;
}

type PublicApiAdminContext = { mode: 'account' } | { mode: 'tenant'; tenantId: string };

function buildBasePath(context: PublicApiAdminContext): string {
  if (context.mode === 'account') {
    return '/api/account/public-api-credentials/';
  }
  return `/api/tenants/${context.tenantId}/public-api-credentials/`;
}

export async function fetchPublicApiCredentials(context: PublicApiAdminContext): Promise<PublicApiCredentialListResponse> {
  const response = await api.get(buildBasePath(context));
  return response.data as PublicApiCredentialListResponse;
}

export async function createPublicApiCredential(
  context: PublicApiAdminContext,
  payload: PublicApiCredentialPayload
): Promise<PublicApiCredentialIssueResponse> {
  const response = await api.post(buildBasePath(context), payload);
  return response.data as PublicApiCredentialIssueResponse;
}

export async function updatePublicApiCredential(
  context: PublicApiAdminContext,
  credentialId: string,
  payload: Partial<PublicApiCredentialPayload>
): Promise<PublicApiCredentialSummary> {
  const response = await api.patch(`${buildBasePath(context)}${credentialId}/`, payload);
  return response.data as PublicApiCredentialSummary;
}

export async function rotatePublicApiCredential(
  context: PublicApiAdminContext,
  credentialId: string
): Promise<PublicApiCredentialIssueResponse> {
  const response = await api.post(`${buildBasePath(context)}${credentialId}/rotate/`);
  return response.data as PublicApiCredentialIssueResponse;
}

export async function revokePublicApiCredential(
  context: PublicApiAdminContext,
  credentialId: string,
  reason?: string
): Promise<PublicApiCredentialSummary> {
  const response = await api.post(`${buildBasePath(context)}${credentialId}/revoke/`, { reason });
  return response.data as PublicApiCredentialSummary;
}

export async function fetchPublicApiCredentialAudit(
  context: PublicApiAdminContext,
  credentialId: string
): Promise<PublicApiCredentialAuditEntry[]> {
  const response = await api.get(`${buildBasePath(context)}${credentialId}/audit/`);
  return response.data as PublicApiCredentialAuditEntry[];
}
